import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, Trash2, GripVertical, Upload, Pencil, AlertTriangle,
  ChevronRight, CheckCircle2, Circle, XCircle, ListChecks,
} from 'lucide-react';
import { confirmDialog, promptDialog } from '../ConfirmDialog';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import AccountAddSearch from './AccountAddSearch';
import ImportFlowModal from './ImportFlowModal';
import PageHeader from '../shared/PageHeader';
import StatLine from '../shared/StatLine';
import './AccountLists.css';

const LISTS_KEY = ['account-lists'];

export default function AccountListsPage() {
  useDocumentTitle('My Account Lists');
  const queryClient = useQueryClient();
  const [selectedListId, setSelectedListId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const lists = useQuery({
    queryKey: LISTS_KEY,
    queryFn: () => apiClient.get('/api/account-lists'),
  });

  const detail = useQuery({
    queryKey: ['account-list', selectedListId],
    queryFn: () => apiClient.get(`/api/account-lists/${selectedListId}`),
    enabled: !!selectedListId,
  });

  // Auto-select first list once they load
  useEffect(() => {
    if (!selectedListId && lists.data && lists.data.length > 0) {
      setSelectedListId(lists.data[0].id);
    }
  }, [lists.data, selectedListId]);

  const createList = useMutation({
    mutationFn: (name) => apiClient.post('/api/account-lists', { name }),
    onSuccess: (created) => {
      logger.accountLists.info('list created', { listId: created.id, name: created.name });
      queryClient.invalidateQueries({ queryKey: LISTS_KEY });
      setSelectedListId(created.id);
    },
    onError: (err) => logger.accountLists.error('create list mutation failed', {}, err),
  });

  const renameList = useMutation({
    mutationFn: ({ id, name }) => apiClient.put(`/api/account-lists/${id}`, { name }),
    onSuccess: (_data, vars) => {
      logger.accountLists.info('list renamed', { listId: vars.id, name: vars.name });
      queryClient.invalidateQueries({ queryKey: LISTS_KEY });
      queryClient.invalidateQueries({ queryKey: ['account-list', selectedListId] });
      setRenaming(false);
    },
    onError: (err) => logger.accountLists.error('rename list mutation failed', {}, err),
  });

  const deleteList = useMutation({
    mutationFn: (id) => apiClient.del(`/api/account-lists/${id}`),
    onSuccess: (_data, id) => {
      logger.accountLists.info('list deleted', { listId: id });
      queryClient.invalidateQueries({ queryKey: LISTS_KEY });
      setSelectedListId(null);
    },
    onError: (err) => logger.accountLists.error('delete list mutation failed', {}, err),
  });

  const saveMembers = useMutation({
    mutationFn: ({ listId, members }) =>
      apiClient.put(`/api/account-lists/${listId}/members`, { members: stripMember(members) }),
    onMutate: ({ listId, members }) => {
      // Optimistic — show the new order/content immediately so drags don't snap back.
      const key = ['account-list', listId];
      const previous = queryClient.getQueryData(key);
      if (previous) queryClient.setQueryData(key, { ...previous, members });
      return { key, previous };
    },
    onError: (err, _vars, ctx) => {
      // Roll back optimistic update on failure.
      if (ctx?.previous) queryClient.setQueryData(ctx.key, ctx.previous);
      logger.accountLists.error('inline save members failed', {}, err);
    },
    onSuccess: (_data, vars) => {
      logger.accountLists.debug('inline members saved', { listId: vars.listId, count: vars.members.length });
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: LISTS_KEY });
      queryClient.invalidateQueries({ queryKey: ['account-list', vars.listId] });
    },
  });

  const handleCreate = async () => {
    const name = await promptDialog({
      title: 'New list',
      placeholder: 'e.g. "T5 GovOps"',
      confirmLabel: 'Create',
    });
    if (name === null || !name.trim()) return;
    createList.mutate(name.trim());
  };

  const handleStartRename = () => {
    setNameDraft(detail.data?.name || '');
    setRenaming(true);
  };

  const handleSubmitRename = (e) => {
    e?.preventDefault?.();
    const next = nameDraft.trim();
    if (next && next !== detail.data?.name) {
      renameList.mutate({ id: selectedListId, name: next });
    } else {
      setRenaming(false);
    }
  };

  const handleDelete = async () => {
    if (!detail.data) return;
    if (!(await confirmDialog(`Delete list "${detail.data.name}"? This can't be undone.`))) return;
    deleteList.mutate(detail.data.id);
  };

  // Member-list edits (add, remove, reorder) all rewrite the full members
  // payload via the bulk PUT endpoint. Full member objects go into the
  // mutation so the optimistic cache patch keeps every resolved field;
  // stripMember runs inside mutationFn.
  const persistMembers = (members) => {
    saveMembers.mutate({ listId: selectedListId, members });
  };

  const handleAddMember = (newMember) => {
    if (!detail.data) return;
    const existing = detail.data.members;
    if (existing.some(m => m.department_id === newMember.department_id)) return;
    // Temp id keeps row keys/sortable stable until the refetch brings the real one.
    persistMembers([...existing, { id: `tmp-${Date.now()}`, ...newMember }]);
  };

  const handleRemoveMember = (memberId) => {
    if (!detail.data) return;
    persistMembers(detail.data.members.filter(m => m.id !== memberId));
  };

  const handleReorder = (orderedIds) => {
    if (!detail.data) return;
    const byId = new Map(detail.data.members.map(m => [m.id, m]));
    const reordered = orderedIds.map(id => byId.get(id)).filter(Boolean);
    persistMembers(reordered);
  };

  return (
    <div className="al-page">
      <PageHeader
        title="My Account Lists"
        blurb="Save named groupings of state agencies — e.g. your Salesforce territory — and use them to filter the dashboard, Spending Flow, and the VCA heatmap."
        actions={
          <button className="al-btn al-btn-primary" onClick={handleCreate}>
            <Plus size={16} /> New list
          </button>
        }
      />

      <div className="al-page-body">
        <aside className="al-list-sidebar" aria-label="Your lists">
          {lists.isLoading && <div className="al-muted al-sidebar-empty">Loading…</div>}
          {lists.error && (
            <div className="al-error al-sidebar-empty">
              <AlertTriangle size={14} /> Failed to load lists
            </div>
          )}
          {!lists.isLoading && lists.data && lists.data.length === 0 && (
            <div className="al-sidebar-empty">
              <ListChecks size={28} className="al-muted-icon" />
              <div>No lists yet</div>
              <button className="al-btn al-btn-primary" onClick={handleCreate}>
                Create your first list
              </button>
            </div>
          )}
          {lists.data?.map(l => {
            const active = l.id === selectedListId;
            return (
              <button
                key={l.id}
                className={`al-sidebar-item ${active ? 'is-active' : ''}`}
                onClick={() => setSelectedListId(l.id)}
              >
                <div className="al-sidebar-item-name">{l.name}</div>
                <div className="al-sidebar-item-meta">
                  {l.member_count} account{l.member_count === 1 ? '' : 's'}
                  {l.member_count > 0 && (
                    <> · {l.matched_count}/{l.member_count} matched</>
                  )}
                </div>
              </button>
            );
          })}
        </aside>

        <main className="al-list-detail">
          {!selectedListId && (
            <div className="al-detail-empty">
              <ListChecks size={48} className="al-muted-icon" />
              <h2>Pick a list</h2>
              <p className="al-muted">Or create one to get started.</p>
            </div>
          )}

          {selectedListId && detail.isLoading && (
            <div className="al-detail-loading">Loading list…</div>
          )}

          {detail.data && (
            <>
              <div className="al-detail-header">
                {renaming ? (
                  <form onSubmit={handleSubmitRename} className="al-rename-form">
                    <input
                      autoFocus
                      className="al-rename-input"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onBlur={handleSubmitRename}
                    />
                  </form>
                ) : (
                  <h2 className="al-detail-title">
                    {detail.data.name}
                    <button className="al-icon-btn" onClick={handleStartRename} title="Rename">
                      <Pencil size={14} />
                    </button>
                  </h2>
                )}

                <div className="al-detail-actions">
                  {/* Quiet outline — "New list" is the page's one filled
                      primary (audit §5) */}
                  <button className="al-btn" onClick={() => setImportOpen(true)}>
                    <Upload size={14} /> Import accounts
                  </button>
                  <button className="al-btn al-btn-danger" onClick={handleDelete} title="Delete list">
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>

              <StatLine
                stats={[
                  { value: detail.data.members.length, label: 'accounts' },
                  { value: detail.data.members.filter(m => m.department_id != null).length, label: 'matched' },
                  { value: new Set(detail.data.members.map(m => m.bu_code).filter(Boolean)).size, label: 'unique BU codes' },
                  countOverlaps(detail.data.members) > 0 && {
                    value: countOverlaps(detail.data.members),
                    label: 'parent-child overlaps',
                  },
                ]}
              />

              <div className="al-add-row">
                <AccountAddSearch
                  existingDepartmentIds={detail.data.members.map(m => m.department_id).filter(Boolean)}
                  onAdd={handleAddMember}
                  disabled={saveMembers.isPending}
                />
              </div>

              <MembersTable
                members={detail.data.members}
                onReorder={handleReorder}
                onRemove={handleRemoveMember}
              />

              {detail.data.members.length === 0 && (
                <div className="al-members-empty">
                  <p>This list is empty. Use the search above to add accounts one at a time, or click <strong>Import accounts</strong> for bulk.</p>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {importOpen && selectedListId && (
        <ImportFlowModal
          listId={selectedListId}
          existingMembers={detail.data?.members || []}
          onClose={() => setImportOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: LISTS_KEY });
            queryClient.invalidateQueries({ queryKey: ['account-list', selectedListId] });
            setImportOpen(false);
          }}
        />
      )}
    </div>
  );
}


function MembersTable({ members, onReorder, onRemove }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  if (members.length === 0) return null;

  const overlapIds = computeOverlapIds(members);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = members.map(m => m.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={members.map(m => m.id)} strategy={verticalListSortingStrategy}>
        <ul className="al-members">
          {members.map(m => (
            <MemberRow
              key={m.id}
              member={m}
              isOverlap={overlapIds.has(m.id)}
              onRemove={() => onRemove(m.id)}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function MemberRow({ member, isOverlap, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: member.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const matched = member.department_id != null;
  const matchIcon = matched
    ? <CheckCircle2 size={14} className="al-icon-good" />
    : <XCircle size={14} className="al-icon-bad" />;

  return (
    <li ref={setNodeRef} style={style} className={`al-member-row ${matched ? '' : 'is-unmatched'}`}>
      <button className="al-drag-handle" {...attributes} {...listeners} title="Drag to reorder">
        <GripVertical size={14} />
      </button>

      <div className="al-member-main">
        <div className="al-member-name">
          {matchIcon}
          <span className="al-member-sf">{member.salesforce_account_name}</span>
          {member.salesforce_territory && (
            <span className="al-member-territory">{member.salesforce_territory}</span>
          )}
        </div>
        {matched && (
          <div className="al-member-resolved">
            {member.acronym && <span className="al-acronym-badge">{member.acronym}</span>}
            <span className="al-member-official">{member.official_name || member.common_name}</span>
            {member.bu_code && <span className="al-bu-code">BU {member.bu_code}</span>}
          </div>
        )}
        {!matched && (
          <div className="al-member-resolved al-muted">
            No SCPRS match — kept as a raw label
          </div>
        )}
        {isOverlap && (
          <div className="al-overlap-badge" title="Another member of this list is the SCPRS parent of this department. The heatmap will show them as separate rows with a warning.">
            <AlertTriangle size={11} /> rolls up to a sibling on this list
          </div>
        )}
      </div>

      <div className="al-member-meta">
        {/* Badge diet (audit slice 7): settled matches (auto/manual) carry
            zero new information per row — they collapse to a small dot
            whose tooltip holds source + confidence. Only states that need
            the user's attention keep a text chip. */}
        {['auto', 'auto_rerank', 'manual'].includes(member.match_source) ? (
          <span
            className={`al-source-dot al-source-dot-${member.match_source}`}
            title={`${member.match_source.replace(/_/g, ' ')}${
              member.match_confidence != null
                ? ` · ${Math.round(member.match_confidence * 100)}% match`
                : ''
            }`}
          />
        ) : (
          <>
            {member.match_confidence != null && (
              <span className="al-confidence-pill">{Math.round(member.match_confidence * 100)}%</span>
            )}
            {member.match_source && (
              <span className={`al-source al-source-${member.match_source}`}>
                {member.match_source.replace(/_/g, ' ')}
              </span>
            )}
          </>
        )}
      </div>

      <span className="ts-row-actions">
        <button className="al-icon-btn al-icon-btn-danger" onClick={onRemove} title="Remove from list">
          <Trash2 size={14} />
        </button>
      </span>
    </li>
  );
}

// Strip frontend-only fields before sending to backend.
function stripMember(members) {
  return members.map(m => ({
    department_id: m.department_id ?? null,
    salesforce_account_name: m.salesforce_account_name,
    salesforce_territory: m.salesforce_territory ?? null,
    match_confidence: m.match_confidence ?? null,
    match_source: m.match_source ?? null,
  }));
}

// A member "overlaps" if another member of the list is its SCPRS parent.
function computeOverlapIds(members) {
  const deptIds = new Set(members.map(m => m.department_id).filter(Boolean));
  const overlapping = new Set();
  for (const m of members) {
    if (m.parent_department_id && deptIds.has(m.parent_department_id)) {
      overlapping.add(m.id);
    }
  }
  return overlapping;
}

function countOverlaps(members) {
  return computeOverlapIds(members).size;
}
